import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react'
import { Phone } from 'lucide-react'
import { Input } from '@shared/components/ui/input'
import { cn } from '@shared/lib/utils'

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string
  onChange?: (value: string) => void
  onValidChange?: (isValid: boolean) => void
}

const PREFIX = '+54 9 '

// Phone number formatting for Argentina (+54 9 area code format)
function formatPhoneNumber(value: string): { formatted: string; isValid: boolean } {
  // Remove the prefix and extract only numbers
  const withoutPrefix = value.replace(PREFIX, '')
  const cleaned = withoutPrefix.replace(/\D/g, '')

  // Remove country code if present (549)
  const withoutCountryCode = cleaned.startsWith('549') && cleaned.length === 13
    ? cleaned.slice(3) // Remove "549" prefix
    : cleaned

  // Argentina format: +54 9 area number
  // Example: +54 9 341 6587546
  let formatted = PREFIX
  let isValid = false

  if (withoutCountryCode.length >= 10) {
    const areaCode = withoutCountryCode.slice(0, 3)
    const restNumber = withoutCountryCode.slice(3, 10)

    formatted = `${PREFIX}${areaCode} ${restNumber}`
    isValid = true
  } else if (withoutCountryCode.length > 0) {
    formatted = `${PREFIX}${withoutCountryCode}`
  }

  return { formatted, isValid }
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value = '', onChange, onValidChange, className, onBlur, onFocus, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState(() => {
      if (!value) return PREFIX
      // Format initial value if provided
      const result = formatPhoneNumber(value.startsWith(PREFIX) ? value : PREFIX + value)
      return result.formatted
    })

    useEffect(() => {
      if (value) {
        // Format incoming value changes
        const result = formatPhoneNumber(value.startsWith(PREFIX) ? value : PREFIX + value)
        if (result.formatted !== displayValue) {
          setDisplayValue(result.formatted)
        }
      } else if (!value && displayValue !== PREFIX) {
        setDisplayValue(PREFIX)
      }
    }, [value, displayValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value

      // If the value doesn't start with prefix, append it to the prefix
      // This handles the case when all text is selected and user types
      if (!newValue.startsWith(PREFIX)) {
        const valueToSet = PREFIX + newValue
        setDisplayValue(valueToSet)
        onChange?.(valueToSet)
        return
      }

      setDisplayValue(newValue)
      onChange?.(newValue)
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select()
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const result = formatPhoneNumber(displayValue)
      setDisplayValue(result.formatted)
      onChange?.(result.formatted)
      onValidChange?.(result.isValid)
      onBlur?.(e)
    }

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="tel"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn('pr-10', className)}
          placeholder="+54 9 341 1234567"
          {...props}
        />
        <Phone className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
      </div>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
